import { Link, usePage } from "@inertiajs/react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import logoUrl from "~/images/nexgen-logo.png"
import "~/styles/site.css"

const NAV = [
  { label: "事業案内", href: "/business" },
  { label: "ソリューション", href: "/solutions" },
  { label: "取扱製品", href: "/products" },
  { label: "会社情報", href: "/company" },
  { label: "お知らせ", href: "/announcements" },
  { label: "検索", href: "/search" },
] as const

function Brand() {
  return (
    <Link className="brand" href="/" aria-label="NEXGEN solution トップ">
      <span className="brand-symbol">
        <img src={logoUrl} alt="" />
      </span>
      <span className="brand-name">
        NEXGEN<small>SOLUTION</small>
      </span>
    </Link>
  )
}

export default function PublicLayout({ children }: { children: ReactNode }) {
  const { url, props, flash } = usePage()
  const [menuOpen, setMenuOpen] = useState(false)
  const toggle = useRef<HTMLButtonElement>(null)
  const isHome = url.split(/[?#]/)[0] === "/"
  const current = (href: string) => url === href || url.startsWith(`${href}/`) || url.startsWith(`${href}?`)

  useEffect(() => {
    setMenuOpen(false)
    document.title = props.meta.full_title
  }, [url, props.meta.full_title])
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false)
        toggle.current?.focus()
      }
    }
    const desktop = window.matchMedia("(min-width: 1101px)")
    const close = () => setMenuOpen(false)
    document.addEventListener("keydown", escape)
    desktop.addEventListener("change", close)
    return () => {
      document.removeEventListener("keydown", escape)
      desktop.removeEventListener("change", close)
    }
  }, [menuOpen])

  return (
    <div className={`nx-site ${isHome ? "is-home" : "is-inner"}`}>
      <a className="skip" href="#main">
        本文へ移動
      </a>
      <header className="header">
        <div className="wrap header-inner">
          <Brand />
          <nav
            className={`nav ${menuOpen ? "open" : ""}`}
            id="navigation"
            aria-label="メインナビゲーション"
            onClick={() => setMenuOpen(false)}
          >
            {NAV.map((item) => (
              <Link key={item.href} href={item.href} aria-current={current(item.href) ? "page" : undefined}>
                {item.label}
              </Link>
            ))}
            <Link
              className="nav-contact"
              href="/contacts/new"
              aria-current={current("/contacts") ? "page" : undefined}
            >
              お問い合わせ
            </Link>
          </nav>
          <button
            ref={toggle}
            className="menu-toggle"
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={menuOpen}
            aria-controls="navigation"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d={menuOpen ? "M5 5l14 14M19 5L5 19" : "M3 8h18M3 16h18"} />
            </svg>
          </button>
        </div>
      </header>
      {flash.notice && (
        <p className="site-notice" role="status">
          {flash.notice}
        </p>
      )}
      {flash.alert && (
        <p className="site-notice" role="alert">
          {flash.alert}
        </p>
      )}
      <main id="main" tabIndex={-1}>
        {children}
      </main>
      <footer className="footer wrap">
        <div className="footer-top">
          <div>
            <Brand />
            <p className="company-address">
              株式会社ネクスジェン・ソリューション
              <br />
              〒107-0062 東京都港区南青山3丁目1-36 青山丸竹ビル6階
              <br />
              <a href="tel:0344465322">TEL 03-4446-5322</a>
            </p>
          </div>
          <nav className="footer-links" aria-label="フッターナビゲーション">
            {NAV.filter((item) => item.href !== "/search").map((item) => (
              <Link key={item.href} href={item.href}>
                {item.label}
              </Link>
            ))}
            <Link href="/case_studies">導入実績</Link>
            <Link href="/contacts/new">お問い合わせ</Link>
            <Link href="/privacy-policy">プライバシーポリシー</Link>
          </nav>
        </div>
        <div className="footer-bottom">
          <span>© {props.current_year} NEXGEN solution Co.,Ltd. All rights reserved.</span>
          <a className="back-top" href="#main">
            ページの先頭へ
          </a>
        </div>
      </footer>
    </div>
  )
}
